// src/components/ProductCard.js
import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { LanguageContext } from "../context/LanguageContext";
import { usePriceVisibility } from "../context/PriceVisibilityContext";
import { useCart } from "../context/CartContext";
import { getProductBySlug } from "../services/products.api";
import { apiRequest } from "../services/api";
import { saveProductReturnPosition } from "../utils/navigationMemory";
import {
  getActiveFreeShippingDiscount,
  getActiveOrderDiscount,
} from "../services/discounts.api";
import { buildFreeShippingInfo } from "../utils/freeShippingDiscount";
import { buildOrderDiscountInfo } from "../utils/orderDiscountInfo";

function ProductCard({ product, imagePriority = false }) {
  const { lang } = useContext(LanguageContext);
  const { pricesSalesVisible } = usePriceVisibility();
  const {
  toggleWishlist,
  isInWishlist,
  addToCart,
  addBxgyDiscountItem,
  cartItems,
  subtotal,
} = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [previewImage, setPreviewImage] = useState(null);

  const [addedModal, setAddedModal] = useState(null);
const [quickAdding, setQuickAdding] = useState(false);

const [canViewPrices, setCanViewPrices] = useState(() =>
  Boolean(localStorage.getItem("esill-token"))
);



useEffect(() => {
  const syncAuthState = () => {
    setCanViewPrices(Boolean(localStorage.getItem("esill-token")));
  };

  syncAuthState();

  window.addEventListener("auth-changed", syncAuthState);
  return () => window.removeEventListener("auth-changed", syncAuthState);
}, []);

const canShowPrices = canViewPrices && pricesSalesVisible;

  const isMobile =
    typeof window !== "undefined" && window.innerWidth < 768;

  const inWishlist = isInWishlist(product.id);

  const resolveMediaUrl = (media) => {
    if (!media) return "/images/placeholder-product.jpg";
    if (typeof media === "string") return media;
    return media.url || media.image || "/images/placeholder-product.jpg";
  };

  const isVideoMedia = (media) => {
    const url = resolveMediaUrl(media);
    const type = typeof media === "object" ? media.media_type : "";

    return type === "video" || /\.(mp4|webm|ogg|mov)$/i.test(url);
  };

  const firstMedia =
    product.images?.[0] ||
    product.image ||
    "/images/placeholder-product.jpg";

  const secondMedia = product.images?.[1] || null;

  const mainMediaUrl = resolveMediaUrl(firstMedia);
  const mainMediaIsVideo = isVideoMedia(firstMedia);

  const secondMediaUrl = resolveMediaUrl(secondMedia);
  const hasSecondHoverImage =
    secondMedia &&
    !mainMediaIsVideo &&
    !isVideoMedia(secondMedia);

  const displayName =
    lang === "AR"
      ? product.nameAR || product.nameEN
      : product.nameEN || product.nameAR;

  const hasDiscount = Number(product.discount) > 0;

  const finalPrice = hasDiscount
    ? product.price * (1 - product.discount / 100)
    : product.price;

  const formatPrice = (value) => {
    const n = Number(value || 0);
    return `$${n.toFixed(2)}`;
  };

  const getBxgyUpsellText = (suggestion) => {
  if (!suggestion) return "";

  const discountText =
    suggestion.discountType === "free"
      ? lang === "AR"
        ? "مجاناً"
        : "FREE"
      : lang === "AR"
      ? `بخصم ${suggestion.discountValue}%`
      : `${suggestion.discountValue}% OFF`;

  const getPublicId =
    suggestion.getPublicId ||
    suggestion.public_id ||
    suggestion.publicId ||
    suggestion.nameEN ||
    "";

  if (suggestion.action === "complete_percentage_same_product") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر واحصلي على الأخيرة ${discountText}`
      : `Add ${suggestion.missingQty} more to get the last one ${discountText}`;
  }

  if (suggestion.action === "add_buy_more") {
    return lang === "AR"
      ? `أضيفي ${suggestion.missingQty} أكثر للحصول على ${suggestion.getQty} ${getPublicId} ${discountText}`
      : `Add ${suggestion.missingQty} more to get ${suggestion.getQty} ${getPublicId} ${discountText}`;
  }

  return lang === "AR"
    ? `أضيفي ${suggestion.getQty} ${getPublicId} إلى السلة ${discountText}`
    : `Add ${suggestion.getQty} ${getPublicId} to cart ${discountText}`;
};

  const bxgyDetails = Array.isArray(product.bxgyDetails)
  ? product.bxgyDetails
  : [];

const buyItem = bxgyDetails.find((i) => i.role === "buy");
const getItem = bxgyDetails.find((i) => i.role === "get");

const isBxgyBuyProduct =
  !!buyItem && Number(buyItem.product_id) === Number(product.id);

const isSameProduct =
  !!buyItem &&
  !!getItem &&
  Number(buyItem.product_id) === Number(getItem.product_id);

const label = (() => {
  if (!isBxgyBuyProduct || !buyItem || !getItem) return "";

  const buy = Number(buyItem.buy_qty || getItem.buy_qty || 0);
  const get = Number(getItem.get_qty || buyItem.get_qty || 0);

  const bxgyDiscountType =
    getItem.discount_type || getItem.get_discount_type || "free";

  const bxgyDiscountValue =
    getItem.discount_value ?? getItem.get_discount_value ?? 0;

  const getPublicId =
    getItem.public_id || getItem.publicId || getItem.name_en || "";

  if (bxgyDiscountType === "free") {
    return lang === "AR"
      ? `اشترِ ${buy} واحصل على ${get} ${getPublicId} مجاناً`
      : `Buy ${buy} and get ${get} ${getPublicId} FREE`;
  }

  return lang === "AR"
    ? `اشترِ ${buy} واحصل على ${get} ${getPublicId} بخصم ${bxgyDiscountValue}%`
    : `Buy ${buy} and get ${get} ${getPublicId} ${bxgyDiscountValue}% OFF`;
})();

  const isNewProduct =
    product.is_new === 1 ||
    product.is_new === true ||
    product.is_new_in === 1 ||
    product.is_new_in === true ||
    product.isNew === true ||
    product.new_arrival === 1 ||
    product.new_arrival === true;

  const isBestSeller =
    product.is_bestseller === 1 ||
    product.is_bestseller === true ||
    product.isBestSeller === true ||
    product.isBestseller === true ||
    product.best_seller === 1 ||
    product.best_seller === true;

  const isQuicklySoldOut =
    product.is_quickly_sold_out === 1 ||
    product.is_quickly_sold_out === true ||
    product.isQuicklySoldOut === true ||
    product.quickly_sold_out === 1 ||
    product.quickly_sold_out === true ||
    product.tags?.includes("QUICKLY_SOLD_OUT");

  const sizeBadges =
    Array.isArray(product.sizes) && product.sizes.length
      ? product.sizes
      : Array.isArray(product.variants)
      ? product.variants
          .map((v) => v.size)
          .filter((size, index, arr) => size && arr.indexOf(size) === index)
      : [];

  const colorBadges =
    Array.isArray(product.colors) && product.colors.length
      ? product.colors
      : Array.isArray(product.variants)
      ? product.variants
          .filter((v) => v.color_name || v.color_hex)
          .reduce((acc, v) => {
            const key = `${v.color_name || ""}-${v.color_hex || ""}`;
            if (!acc.some((x) => x.key === key)) {
              acc.push({
                key,
                name: v.color_name || "",
                code: v.color_hex || "#8B3A2F",
              });
            }
            return acc;
          }, [])
      : [];

  const getSizeLabel = (size) => {
    if (typeof size === "string" || typeof size === "number") {
      return String(size).toUpperCase();
    }

    return String(
      size?.size ||
        size?.label ||
        size?.name ||
        size?.value ||
        ""
    ).toUpperCase();
  };

  const getColorCode = (color) => {
    if (typeof color === "string") return color;
    return color?.code || color?.color_hex || color?.hex || "#8B3A2F";
  };

 const getProductSlug = () => {
  return (
    product?.seo_slug ||
    product?.slug ||
    product?.product_slug ||
    ""
  );
};

const normalizeCategorySlug = (value) => {
  if (!value) return "";

  const rawValue =
    typeof value === "object"
      ? value.slug ||
        value.category_slug ||
        value.name_en ||
        value.name ||
        ""
      : value;

  return String(rawValue)
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

const getSourceCategory = () => {
  const directCategory =
    product?.category_slug ||
    product?.primary_category ||
    product?.category;

  const directSlug = normalizeCategorySlug(directCategory);

  if (directSlug) return directSlug;

  if (Array.isArray(product?.categories) && product.categories.length > 0) {
    return normalizeCategorySlug(product.categories[0]);
  }

  return "";
};

const getProductUrl = () => {
  const slug = getProductSlug();

  if (!slug) return "/all-products";

  const params = new URLSearchParams(location.search);
  const sourceCategory = getSourceCategory();

  if (
    sourceCategory &&
    !params.get("sourceCategory") &&
    !params.get("selectedCategory") &&
    !params.get("category")
  ) {
    params.set("sourceCategory", sourceCategory);
  }

  const query = params.toString();
  const base = `/product/${encodeURIComponent(slug)}`;

  return `${base}${query ? `?${query}` : ""}`;
};

  const handleOpenDetails = (e) => {
  saveProductReturnPosition({
    productId: product.id,
    element: e?.currentTarget,
  });

  navigate(getProductUrl(), {
    state: {
      fromProductCard: true,
      sourcePath: `${location.pathname}${location.search}`,
    },
  });
};

  const handleToggleWishlist = (e) => {
    e.stopPropagation();

    toggleWishlist({
      id: product.id,
      nameEN: product.nameEN,
      nameAR: product.nameAR,
      price: canShowPrices ? finalPrice : 0,
      image: mainMediaUrl,
    });
  };

  const stockVariants = Array.isArray(product.variants)
    ? product.variants
    : [];

  const hasAvailableVariant = stockVariants.some(
    (variant) =>
      Number(
        variant?.stock_qty ??
          variant?.stock ??
          variant?.quantity ??
          variant?.qty ??
          0
      ) > 0
  );

  const explicitTotalStock = Number(
    product.totalStock ??
      product.total_stock ??
      product.stock ??
      product.stock_qty ??
      0
  );

  const isOutOfStock =
    stockVariants.length > 0
      ? !hasAvailableVariant
      : explicitTotalStock <= 0;

  const totalStock = isOutOfStock
    ? 0
    : stockVariants.length > 0
    ? stockVariants.reduce(
        (sum, variant) =>
          sum +
          Math.max(
            0,
            Number(
              variant?.stock_qty ??
                variant?.stock ??
                variant?.quantity ??
                variant?.qty ??
                0
            )
          ),
        0
      )
    : explicitTotalStock;

  const outOfStockBadge = isOutOfStock
    ? lang === "AR"
      ? "نفد المخزون"
      : "Out of stock"
    : "";

const shouldCheckAvailability =
  isOutOfStock ||
  sizeBadges.length === 0 ||
  colorBadges.length === 0;

  const isLoggedInUser = () => {
  return Boolean(localStorage.getItem("esill-token"));
};

const openLoginRequiredModal = () => {
  localStorage.removeItem("esill-open-login");
  window.dispatchEvent(new Event("open-entry-modal"));
};

const handleAddToCartClick = async (e) => {
  e.stopPropagation();

  if (!isLoggedInUser()) {
    openLoginRequiredModal();
    return;
  }

  if (shouldCheckAvailability) {
    navigate(getProductUrl());
    return;
  }

  if (quickAdding) return;

  setQuickAdding(true);

  try {
    const fullProduct = await getProductBySlug(getProductSlug());

    const firstVariant =
      fullProduct?.variants?.find((v) => Number(v.stock_qty) > 0) ||
      fullProduct?.variants?.[0] ||
      null;

if (!firstVariant) {
  navigate(getProductUrl());
  return;
}

    const basePrice = Number(fullProduct.base_price || product.price || 0);
    const salePercent = Number(fullProduct.sale_percent || product.discount || 0);
    const isOnSale = Boolean(fullProduct.on_sale || salePercent > 0);

    const productPrice = isOnSale
      ? Math.round(basePrice * (1 - salePercent / 100))
      : basePrice;

    const itemPrice = productPrice + Number(firstVariant.extra_price || 0);

   const updatedCart = await addToCart({
  product_id: fullProduct.id,
  variant_id: firstVariant.id,
  bxgyDetails: fullProduct.bxgy_details || fullProduct.bxgyDetails || [],

  public_id: fullProduct.public_id,
  nameEN: fullProduct.name_en,
  nameAR: fullProduct.name_ar,
  price: canShowPrices ? itemPrice : 0,
  image: mainMediaUrl,
  size: firstVariant.size,
  color: firstVariant.color_name,
  color_hex: firstVariant.color_hex || "#000000",
});

apiRequest("/analytics/track", {
  method: "POST",
  body: JSON.stringify({
    action_type: "add_to_cart",
    entity_type: "product",
    entity_id: fullProduct.id,
  }),
}).catch(() => {});

    let freeShippingDiscount = null;
let orderDiscount = null;

try {
  freeShippingDiscount = await getActiveFreeShippingDiscount();
} catch {}

try {
  orderDiscount = await getActiveOrderDiscount();
} catch {}

    const newCartCount =
  updatedCart?.totalItems ??
  cartItems.reduce((sum, item) => sum + Number(item.qty || 0), 0) + 1;

const newSubtotal =
  updatedCart?.subtotal ??
  Number(subtotal || 0) + Number(itemPrice || 0);

    const orderDiscountInfo = buildOrderDiscountInfo({
  discount: orderDiscount,
  subtotal: newSubtotal,
  totalItems: newCartCount,
  lang,
});

const bxgySuggestion =
  updatedCart?.bxgySuggestion ||
  updatedCart?.suggestion ||
  null;

setAddedModal({
  image: mainMediaUrl,
  isVideo: mainMediaIsVideo,
  count: newCartCount,
  subtotal: newSubtotal,
  total: orderDiscountInfo.finalTotal,
  bxgySuggestion,
  freeShippingInfo: buildFreeShippingInfo({
    discount: freeShippingDiscount,
    subtotal: newSubtotal,
    totalItems: newCartCount,
    lang,
  }),
  orderDiscountInfo,
});
  } catch (err) {
  console.error(err);
  navigate(getProductUrl());
} finally {
  setQuickAdding(false);
}
};

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        onClick={handleOpenDetails}
data-product-card-id={product.id}
        dir={lang === "AR" ? "rtl" : "ltr"}
        className="
          group
          w-full
          h-full
          bg-white
          overflow-hidden
          cursor-pointer
          flex flex-col
          text-start
        "
      >
        {/* MEDIA */}
        <div className="relative w-full overflow-hidden bg-gray-100">
            {/* OUT OF STOCK BADGE */}
            {outOfStockBadge && (
              <div
                data-etika-out-of-stock-badge="true"
                className={`
                  absolute top-3 z-40
                  ${lang === "AR" ? "right-3" : "left-3"}
                  px-3 py-1.5
                  bg-[#1e1e1e]
                  text-white
                  text-[10px] sm:text-[11px]
                  font-semibold
                  uppercase
                  tracking-[0.08em]
                  shadow-md
                  pointer-events-none
                `}
              >
                {outOfStockBadge}
              </div>
            )}

          {mainMediaIsVideo ? (
            <video
  src={mainMediaUrl}
  muted
  loop
  autoPlay
  playsInline
  preload={imagePriority ? "metadata" : "none"}
  className="
    w-full
    aspect-[3/4]
    object-cover
    block
  "
/>
          ) : (
            <img
  src={mainMediaUrl}
  alt={displayName}
  loading={imagePriority ? "eager" : "lazy"}
  fetchPriority={imagePriority ? "high" : "auto"}
  decoding="async"
    onError={(event) => {
      if (event.currentTarget.dataset.etikaFallback === "1") return;

      event.currentTarget.dataset.etikaFallback = "1";
      event.currentTarget.src = "/assets/logo.jpg";
      event.currentTarget.style.objectFit = "contain";
      event.currentTarget.style.padding = "24px";
      event.currentTarget.style.backgroundColor = "#ffffff";
    }}
  className={`
    w-full
    aspect-[3/4]
    object-cover
    block
    ${
      !isMobile
        ? "transition-all duration-500 group-hover:scale-[1.02]"
        : ""
    }
  `}
/>
          )}

          {/* DESKTOP SECOND IMAGE ON HOVER */}
          {hasSecondHoverImage && (
            <img
  src={secondMediaUrl}
  alt={displayName}
  loading="lazy"
  decoding="async"
    onError={(event) => {
      if (event.currentTarget.dataset.etikaFallback === "1") return;

      event.currentTarget.dataset.etikaFallback = "1";
      event.currentTarget.src = "/assets/logo.jpg";
      event.currentTarget.style.objectFit = "contain";
      event.currentTarget.style.padding = "24px";
      event.currentTarget.style.backgroundColor = "#ffffff";
    }}
  className="
    hidden md:block
    absolute inset-0
    w-full h-full
    object-cover
    opacity-0
    group-hover:opacity-100
    transition-opacity duration-500
  "
/>
          )}

          {/* DESKTOP ADD TO CART HOVER BUTTON */}
<button
  type="button"
  onClick={handleAddToCartClick}
  disabled={quickAdding}
  className="
    hidden md:flex
    group/addcart
    absolute bottom-0 left-0 right-0 z-40
    h-11
    items-center justify-center
    bg-black/55
    text-white
    opacity-0
    pointer-events-none
    group-hover:opacity-100
    group-hover:pointer-events-auto
    transition-all duration-300
    overflow-hidden
    disabled:opacity-70 disabled:cursor-wait
  "
>
  <span
    className="
      absolute
      text-[13px]
      font-medium
      tracking-[0.18em]
      uppercase
      transition-all duration-300
      opacity-100 translate-y-0
      group-hover/addcart:opacity-0
      group-hover/addcart:-translate-y-3
    "
  >
    {shouldCheckAvailability
  ? lang === "AR"
    ? "تحقق من التوفر"
    : "Check availability"
  : quickAdding
  ? lang === "AR"
    ? "جارٍ الإضافة"
    : "Adding"
  : lang === "AR"
  ? "أضف إلى السلة"
  : "Add to cart"}
  </span>

  <svg
    className={`
  opacity-0 translate-y-3
  transition-all duration-300
  ${shouldCheckAvailability ? "" : "group-hover/addcart:opacity-100 group-hover/addcart:translate-y-0"}
`}
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="9" cy="21" r="1" />
    <circle cx="20" cy="21" r="1" />
    <path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h8.8a2 2 0 0 0 2-1.6L23 6H6" />
  </svg>
</button>

          {/* SALE BADGE */}
{canShowPrices && hasDiscount && (
  <div
    className="
      absolute top-3 right-0 z-20
      px-2 py-[4px]
      bg-red-600 text-white
      text-[11px] sm:text-[12px]
      font-bold tracking-wide
      uppercase
      leading-none
      transition-opacity duration-300
      md:group-hover:opacity-0
    "
  >
    -{Number(product.discount)}%
  </div>
)}

          {/* BXGY BAR - kept safe */}
          {isBxgyBuyProduct && canShowPrices && label && (
            <div className="absolute left-0 right-0 top-0 z-10 group/bxgy">
              <div
                className="
                  px-3 py-2
                  text-white text-[10px] sm:text-[11px] font-semibold
                  flex items-center justify-between gap-2
                "
                style={{
                  background:
                    "linear-gradient(90deg, rgba(142,36,170,0.92), rgba(94,53,177,0.92))",
                }}
              >
                <span className="flex-1 text-center line-clamp-1">
                  {label}
                </span>

                {!isSameProduct && getItem?.image && (
                  <img
  src={getItem.image}
  alt=""
  loading="lazy"
  decoding="async"
  onClick={(e) => {
    e.stopPropagation();
    setPreviewImage(getItem.image);
  }}
  className="
    w-7 h-7 object-cover
    border border-white/30
    cursor-pointer
  "
/>
                )}
              </div>
            </div>
          )}

          {/* NEW / BESTSELLER BADGES */}
          {(isNewProduct || isBestSeller) && (
            <div
              className="
                absolute bottom-0 left-0 z-20
                flex flex-col items-start gap-0
                transition-opacity duration-300
                md:group-hover:opacity-0
              "
            >
              {isNewProduct && (
                <span
                  className="
                    w-[82px]
                    px-2 py-[5px]
                    bg-[#ff2bd6]
                    text-white
                    text-[10px] sm:text-[11px]
                    font-normal
                    tracking-wide
                    uppercase
                    leading-none
                    text-left
                  "
                >
                  {lang === "AR" ? "جديد" : "NEW"}
                </span>
              )}

              {isBestSeller && (
                <span
                  className="
                    w-[82px]
                    px-2 py-[5px]
                    bg-[#9a4a23]
                    text-white
                    text-[10px] sm:text-[11px]
                    font-normal
                    tracking-wide
                    uppercase
                    leading-none
                    text-left
                  "
                >
                  {lang === "AR" ? "الأكثر مبيعاً" : "BESTSELLER"}
                </span>
              )}
            </div>
          )}

          {/* WISHLIST */}
<button
  type="button"
  onClick={handleToggleWishlist}
  className="
    absolute bottom-5 md:bottom-14 right-3 z-30
    w-8 h-8 md:w-9 md:h-9
    rounded-full
    bg-white/85
    backdrop-blur-sm
    flex items-center justify-center
    shadow-sm
    active:scale-95
    transition
  "
  aria-label={lang === "AR" ? "المفضلة" : "Wishlist"}
>
  <span
    className={`
      text-[22px] md:text-[24px]
      leading-none
      font-light
      transition
      ${inWishlist ? "text-red-500" : "text-black"}
    `}
  >
    {inWishlist ? "♥" : "♡"}
  </span>
</button>
        </div>

        {/* DETAILS */}
        <div
          className="
            bg-white
            pt-3 pb-2
            md:pt-4 md:pb-3
            md:grid
            md:grid-cols-[minmax(0,1fr)_auto]
            md:gap-x-4
            md:items-start
          "
        >
          {/* LEFT SIDE: NAME / PRICE / SELLING FAST */}
          <div className="min-w-0">
            {/* NAME */}
            <p
              className="
                text-[15px] sm:text-[16px]
                md:text-[15px]
                font-normal
                leading-[1.25]
                text-[#1f1f1f]
                line-clamp-2
                min-h-[38px] sm:min-h-[42px]
                md:min-h-0
              "
            >
              {displayName}
            </p>

           {/* PRICE */}
<div className="mt-2 md:mt-1 flex items-center gap-2">
  {canShowPrices ? (
    <>
      <span
        className="
          text-[17px] sm:text-[19px]
          md:text-[16px]
          font-normal
          text-[#1f1f1f]
          tracking-wide
        "
      >
        {formatPrice(finalPrice)}
      </span>

      {canShowPrices && hasDiscount && (
        <span className="text-[12px] line-through text-[#1f1f1f]">
          {formatPrice(product.price)}
        </span>
      )}
    </>
  ) : (
    <span
      className="
        text-[13px]
        md:text-[12px]
        font-semibold
        text-[#1f1f1f]
        uppercase
        tracking-wide
      "
    >
      {lang === "AR" ? "سجّل الدخول لمشاهدة السعر" : "Login to view price"}
    </span>
  )}
</div>

            {/* DESKTOP SELLING FAST */}
{isQuicklySoldOut && (
  <div
    className="
      hidden md:block
      mt-2
      text-[12px]
      font-bold
      tracking-wide
      text-red-600
      uppercase
    "
  >
    {lang === "AR" ? "يُباع بسرعة 🔥" : "SELLING FAST 🔥"}
  </div>
)}
          </div>

          {/* RIGHT SIDE ON DESKTOP / NORMAL FLOW ON MOBILE */}
          <div
            className="
              mt-2 md:mt-0
              md:flex md:flex-col md:items-end
              md:min-w-[88px]
            "
          >
            {/* SIZES */}
            {sizeBadges.length > 0 && (
              <div className="flex flex-wrap gap-1.5 md:justify-end">
                {sizeBadges.slice(0, 5).map((size, idx) => {
                  const sizeLabel = getSizeLabel(size);
                  if (!sizeLabel) return null;

                  return (
                    <span
                      key={idx}
                      className="
                        inline-flex items-center justify-center
                        min-w-[30px]
                        h-[22px]
                        px-1 py-1
                        border border-black
                        bg-white
                        text-[12px]
                        font-normal
                        text-[#1f1f1f]
                        uppercase
                        leading-none
                      "
                    >
                      {sizeLabel}
                    </span>
                  );
                })}
              </div>
            )}

            {/* COLORS */}
            {colorBadges.length > 0 && (
              <div className="mt-3 md:mt-2 flex items-center gap-2 md:justify-end">
                {colorBadges.slice(0, 5).map((color, idx) => (
                  <span
                    key={idx}
                    title={color?.name || ""}
                    className="
                      w-[12px] h-[12px]
                      rounded-full
                      border border-black/10
                      inline-block
                    "
                    style={{
                      backgroundColor: getColorCode(color),
                    }}
                  />
                ))}
              </div>
            )}

            {/* MOBILE SOLD QUICKLY */}
{isQuicklySoldOut && (
  <div
    className="
      md:hidden
      mt-2
      text-[12px]
      font-bold
      tracking-wide
      text-red-600
      uppercase
    "
  >
    {lang === "AR" ? "يُباع بسرعة 🔥" : "SOLD QUICKLY 🔥"}
  </div>
)}
          </div>
        </div>
      </div>

{/* ADDED TO CART MODAL */}
{addedModal && (
  <div
    className="
      fixed inset-0 z-[999999]
      bg-black/20
      flex items-center justify-center
      px-4
    "
    onClick={() => setAddedModal(null)}
  >
    <div
      dir={lang === "AR" ? "rtl" : "ltr"}
      className="
        w-full max-w-[520px]
        bg-white 
        rounded-md
        shadow-[0_18px_55px_rgba(0,0,0,0.25)]
        border border-gray-200
      "
      onClick={(e) => e.stopPropagation()}
    >
      {/* HEADER */}
      <div className="relative px-5 py-4">
        <h3 className="text-[18px] md:text-[20px] font-normal text-gray-900">
          {lang === "AR" ? "تمت الإضافة إلى السلة" : "Added to cart"}
        </h3>

        <button
          type="button"
          onClick={() => setAddedModal(null)}
          className="
            absolute top-3 right-3
            w-6 h-6
            rounded-full
            bg-gray-500
            text-white
            flex items-center justify-center
            text-[16px] leading-none
            hover:bg-gray-700
            transition
          "
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* DETAILS */}
      <div className="bg-[#f3f3f3] px-5 py-5">
        <div className="grid grid-cols-[110px_1fr] gap-6 items-center">
          <div className="bg-white border border-gray-300 p-1">
  {addedModal.isVideo ? (
    <video
      src={addedModal.image}
      muted
      playsInline
      preload="metadata"
      className="w-full aspect-square object-cover"
    />
  ) : (
    <img
  src={addedModal.image}
  alt=""
  loading="lazy"
  decoding="async"
  className="w-full aspect-square object-cover"
/>
  )}
</div>

          <div className="text-[12px] md:text-[13px] text-gray-800 space-y-2">
            <div className="flex justify-between gap-4">
              <span>
                {lang === "AR"
                  ? "عدد المنتجات في سلتك"
                  : "Number of products in your cart"}
              </span>
              <span className="font-semibold">{addedModal.count}</span>
            </div>

            <div className="flex justify-between gap-4">
              <span>{lang === "AR" ? "مجموع المنتجات" : "Total products"}</span>
              <span className="font-semibold">
                {canShowPrices
  ? formatPrice(addedModal.subtotal)
  : lang === "AR"
  ? "السعر مخفي"
  : "Price hidden"}
              </span>
            </div>

{addedModal.orderDiscountInfo?.valid &&
  Number(addedModal.orderDiscountInfo.discountAmount || 0) > 0 && (
    <div className="flex justify-between gap-4 text-green-700">
      <span>{lang === "AR" ? "خصم الطلب" : "Order discount"}</span>
      <span className="font-semibold">
        {canShowPrices
  ? `-${formatPrice(addedModal.orderDiscountInfo.discountAmount)}`
  : lang === "AR"
  ? "السعر مخفي"
  : "Price hidden"}
      </span>
    </div>
)}

            <div className="flex justify-between gap-4">
              <span>{lang === "AR" ? "الإجمالي" : "Together"}</span>
              <span className="font-semibold">
                {canShowPrices
  ? formatPrice(addedModal.total)
  : lang === "AR"
  ? "السعر مخفي"
  : "Price hidden"}
              </span>
            </div>

            {canShowPrices && addedModal.freeShippingInfo?.hasDiscount && (
  <div
    className={`
      mt-2
      text-white
      px-3 py-2
      text-[11px] md:text-[12px]
      font-semibold
      ${
        addedModal.freeShippingInfo.valid
          ? "bg-[#10b981]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.freeShippingInfo.label}

    {addedModal.freeShippingInfo.endLabel && (
      <div className="mt-1 text-[10px] md:text-[11px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.freeShippingInfo.endLabel}`
          : `Ends on ${addedModal.freeShippingInfo.endLabel}`}
      </div>
    )}
  </div>
)}

{canShowPrices && addedModal.orderDiscountInfo?.hasDiscount && (
  <div
    className={`
      mt-2
      text-white
      px-3 py-2
      text-[11px] md:text-[12px]
      font-semibold
      ${
        addedModal.orderDiscountInfo.valid
          ? "bg-[#2563eb]"
          : "bg-[#f59e0b]"
      }
    `}
  >
    {addedModal.orderDiscountInfo.label}

    {addedModal.orderDiscountInfo.endLabel && (
      <div className="mt-1 text-[10px] md:text-[11px] font-medium opacity-90">
        {lang === "AR"
          ? `ينتهي في ${addedModal.orderDiscountInfo.endLabel}`
          : `Ends on ${addedModal.orderDiscountInfo.endLabel}`}
      </div>
    )}
  </div>
)}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className="px-5 py-5">
        {addedModal?.bxgySuggestion && (
  <button
    type="button"
    onClick={async () => {
      await addBxgyDiscountItem(addedModal.bxgySuggestion);
      setAddedModal(null);
    }}
    className="
      mb-3
      w-full
      h-11
      bg-green-700
      text-white
      text-[13px]
      font-semibold
      rounded-md
      hover:bg-green-800
      transition
    "
  >
    {getBxgyUpsellText(addedModal.bxgySuggestion)}
  </button>
)}
        <div className="flex items-center justify-center gap-2 md:gap-3">
          <button
            type="button"
            onClick={() => setAddedModal(null)}
            className="
              flex-1
              h-11
              border border-gray-700
              bg-white
              text-gray-700
              text-[13px]
              hover:bg-gray-50
              transition
            "
          >
            {lang === "AR" ? "العودة إلى المتجر" : "Back to the store"}
          </button>

          <button
            type="button"
            onClick={() => {
  apiRequest("/analytics/track", {
    method: "POST",
    body: JSON.stringify({
      action_type: "open_cart_page",
      entity_type: "cart",
    }),
  }).catch(() => {});

  setAddedModal(null);
  navigate("/cart");
}}
            className="
              flex-1
              h-11
              bg-[#e5ad20]
              text-white
              text-[13px]
              font-medium
              hover:bg-[#d59d17]
              transition
            "
          >
            {lang === "AR" ? "اذهب إلى السلة »" : "Go to cart »"}
          </button>
        </div>

        <p className="mt-5 text-[12px] md:text-[13px] text-gray-600 leading-relaxed">
          {lang === "AR"
            ? "لا تؤجل عملية الشراء، إضافة المنتجات إلى السلة لا تعني أنها محجوزة لك."
            : "Don't delay your purchase, adding items to your cart does not mean you have reserved them."}
        </p>
      </div>
    </div>
  </div>
)}

      {/* BXGY PREVIEW MODAL */}
      {previewImage && (
        <div
          onClick={() => setPreviewImage(null)}
          className="
            fixed inset-0 z-[999]
            bg-black/70 flex items-center justify-center
          "
        >
          <div className="relative">
            <img
              src={previewImage}
              alt=""
              className="
                max-w-[90%] max-h-[80%]
                rounded-xl shadow-2xl
                animate-fadeIn
              "
            />

            {getItem && (
              <div
                className="
                  absolute top-3 left-3
                  px-3 py-1 rounded-full
                  text-white text-xs font-bold
                  shadow-md
                "
                style={{
                  background: "linear-gradient(135deg,#E53935,#FB8C00)",
                }}
              >
                {getItem.discount_type === "free"
                  ? lang === "AR"
                    ? "احصل عليه مجاناً"
                    : "Get this FREE"
                  : lang === "AR"
                  ? `خصم ${getItem.discount_value}%`
                  : `${getItem.discount_value}% OFF`}
              </div>
            )}
          </div>
        </div>
      )}

    </>
  );
}

export default ProductCard;
